import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, AlertCircle } from "lucide-react";
import { setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const ResetPassword = () => {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pwd.length < 6) return setErr("Password must be at least 6 characters.");
    if (pwd !== confirm) return setErr("Passwords don't match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pwd });
      setToken(data.access_token);
      setUser(data.user);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not reset password. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bb-bg text-white flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-gradient-to-br from-[#1c3df5]/20 to-transparent blur-3xl pointer-events-none"></div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-white/95"></div>
          </div>
          <span className="font-display font-bold text-2xl tracking-tight">Northwind</span>
        </Link>
        <div className="bb-card rounded-3xl p-7 lg:p-8" data-testid="reset-password-card">
          <h1 className="font-display font-bold text-[26px] text-white mb-1">Set a new password</h1>
          <p className="text-[13px] text-white/55 mb-6">Choose a strong password for your account.</p>

          {!token ? (
            <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300" data-testid="reset-no-token">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span>
                This link is missing its token.{" "}
                <Link to="/forgot-password" className="underline">Request a new reset link</Link>.
              </span>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-[12px] text-white/55 mb-1.5 block">New password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    placeholder="At least 6 characters"
                    data-testid="reset-password-input"
                    className="w-full h-11 px-4 pr-10 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white"
                    tabIndex={-1}
                  >
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[12px] text-white/55 mb-1.5 block">Confirm password</label>
                <input
                  type={show ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="Re-enter password"
                  data-testid="reset-confirm-input"
                  className="w-full h-11 px-4 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
                />
              </div>
              {err && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full h-11 rounded-xl bb-blue text-white font-medium text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Updating..." : (<>Reset password <ArrowRight size={15} /></>)}
              </button>
            </form>
          )}
          <div className="mt-6 pt-5 border-t border-white/5 text-center text-[13px] text-white/55">
            <Link to="/login" className="text-[#7d8eff] hover:underline font-medium">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
